import {
    createPdf,
    createCover,
    createBackCover,
    createPage,
    createHeading,
    createParagraph,
    createHtml,
    createInput,
    addPageHeaders,
    addPageNumbers,
    addFootnotes,
    download as downloadPdf,
    createTable,
    toTable,
    convertHeadersToHead,
    convertRowsToBody,
    output as outputPdf
} from '@/lib/pdf';

import {
    createXlsx,
    createAoaSheet,
    createJsonSheet,
    appendAoaToSheet,
    appendJsonToSheet,
    addSheet,
    download as downloadXlsx,
    toXlsx,
    convertRowsToJson
} from '@/lib/xlsx';

import { BaseModel, Investor } from '@/models';
import dialog from '@/lib/helpers/dialog';

export default {
    methods: {
        // PDF
        createPdf,
        createCover,
        createBackCover,
        createPage,
        createHeading,
        createParagraph,
        createInput,
        addPageHeaders,
        addPageNumbers,
        addFootnotes,
        downloadPdf,
        outputPdf,
        createTable,
        toTable,
        convertHeadersToHead,
        convertRowsToBody,
        // XLSX
        createXlsx,
        createAoaSheet,
        createJsonSheet,
        appendAoaToSheet,
        appendJsonToSheet,
        addSheet,
        downloadXlsx,
        toXlsx,
        convertRowsToJson,
        // Misc
        preExport(callback = () => {}) {
            dialog({
                title: this.$t('are_you_sure'),
                text: this.$t('this_export_takes_a_significant_amount_of_time'),
                onContinueFn: callback,
                hideBeforeContinue: true
            });
        },
        getHeaders(headers, exclude = []) {
            return headers.filter(h => {
                let text = typeof h === 'string' ? h : h.text;
                let value = typeof h === 'string' ? h : h.value;

                if (exclude.includes(text)) {
                    return false;
                }

                if (exclude.includes(value)) {
                    return false;
                }

                return true;
            });
        },
        async collectItems(requestItems = null, options = {}) {
            if (!requestItems) {
                return;
            }

            options = {
                id: null,
                filters: {},
                limit: 100,
                sort: [],
                beforeRequest: () => {},
                afterRequest: () => {},
                ...options
            };

            let items = [];
            let total = 1;
            let offset = 0;

            try {
                while (items.length < total) {
                    if (options.id && this.$store.getters['app/aborted'].includes(options.id)) {
                        return;
                    }

                    options.beforeRequest(items.length, total, options.limit);

                    const response = await requestItems(offset, options.limit, options.sort, options.filters);

                    if (response === null) {
                        return;
                    }

                    if (!('data' in response)) {
                        throw new Error(this.$t('response_not_recognised'));
                    }

                    if (!('metadata' in response)) {
                        if (!Array.isArray(response.data)) {
                            throw new Error(this.$t('response_not_recognised'));
                        } else {
                            total = response.data.length;
                        }
                    } else if (response.metadata && response.metadata.total !== null) {
                        total = response.metadata.total;
                    } else {
                        total = 0;
                    }

                    items.push(...response.data);
                    offset = items.length;

                    options.afterRequest(items.length, total, options.limit);
                }
            } catch (error) {
                console.error(error);
            }

            return items;
        },
        async appendTableToPdf(pdf, options = {}, callback = null) {
            options = {
                id: null,
                items: [],
                request: null,
                headers: [],
                filters: {},
                defaultHeaders: [],
                excludeHeaders: [],
                createNewPage: true,
                transformRow: row => row,
                collectOptions: {},
                heading: null,
                ...options
            };

            let rows = null;

            if (options.items && options.items.length) {
                rows = options.items;
            } else {
                rows = await this.collectItems(options.request, {
                    id: options.id,
                    filters: options.filters,
                    ...options.collectOptions
                });
            }

            if (!rows || rows.length === 0) {
                return pdf;
            }

            if (options.transformRow && typeof options.transformRow === 'function') {
                rows = rows.map(options.transformRow);
            }

            if (options.cast && options.cast.prototype instanceof BaseModel) {
                rows = rows.map(row => (row instanceof BaseModel ? row : new options.cast(row)));
            }

            const excludeHeaders = [
                ...options.excludeHeaders,
                ...options.headers
                    .filter(h => {
                        if (h.hidden) return true;
                        if (!h.active) return true;
                        if (options.defaultHeaders.length) {
                            return !options.defaultHeaders.includes(h.value);
                        }
                        return false;
                    })
                    .map(h => h.value)
            ];

            const headers = this.getHeaders(options.headers, excludeHeaders);

            const head = this.convertHeadersToHead([...headers]);
            const body = this.convertRowsToBody(rows, head);
            const columns = head.map(cell => ({
                header: cell.content,
                dataKey: cell.header.value
            }));

            if (options.createNewPage) {
                pdf = this.createPage(pdf);
            }

            pdf = await this.createTable({ head: [head], columns, body, ...options }, pdf);

            if (callback) {
                pdf = await callback(pdf, rows);
            }

            return pdf;
        },
        async toXlsxJson() {
            return null;
        },
        async getJsonForXlsx(options = {}, callback = null) {
            options = {
                id: null,
                items: [],
                request: null,
                headers: [],
                filters: {},
                defaultHeaders: [],
                excludeHeaders: [],
                name: null,
                collectOptions: {},
                ...options
            };

            let rows = null;

            if (options.items && options.items.length) {
                rows = options.items;
            } else {
                rows = await this.collectItems(options.request, {
                    id: options.id,
                    filters: options.filters,
                    ...options.collectOptions
                });
            }

            if (!rows || rows.length === 0) {
                return null;
            }

            if (options.cast && options.cast.prototype instanceof BaseModel) {
                rows = rows.map(row => (row instanceof BaseModel ? row : new options.cast(row)));
            }

            const excludeHeaders = [
                ...options.excludeHeaders,
                ...options.headers
                    .filter(h => {
                        if (h.hidden) return true;
                        if (options.defaultHeaders.length) {
                            return !options.defaultHeaders.includes(h.value);
                        }
                        return false;
                    })
                    .map(h => h.value)
            ];

            const headers = this.getHeaders(options.headers, excludeHeaders);
            const json = this.convertRowsToJson(rows, [...headers]);
            const headerStrings = headers.map(header => header.text);

            let children = [];

            if (callback) {
                children = await callback(rows);
            }

            return { headers: headerStrings, data: json, name: options.name, children };
        },
        async addToXlsx(workbook, name, json = null) {
            let worksheet = this.createAoaSheet(
                workbook,
                [
                    [
                        {
                            v: name,
                            s: { font: { bold: true, sz: 24 } }
                        }
                    ]
                ],
                {
                    origin: { r: 1, c: 1 }
                }
            );

            json = json ? json : await this.toXlsxJson();

            if (!json) {
                return workbook;
            }

            worksheet = this.appendJsonToSheet(worksheet, json.data, {
                origin: { r: 3, c: 1 },
                header: json.headers,
                skipHeader: false
            });

            for (const child of json.children) {
                if (child) {
                    worksheet = this.appendAoaToSheet(
                        worksheet,
                        [
                            [],
                            [
                                {
                                    v: 'name' in child ? child.name : '',
                                    s: { font: { bold: true, sz: 16 } }
                                }
                            ]
                        ],
                        {
                            origin: { r: -1, c: 1 }
                        }
                    );
                    worksheet = this.appendJsonToSheet(worksheet, child.data, {
                        origin: { r: -1, c: 1 },
                        header: child.headers,
                        skipHeader: false
                    });
                }
            }

            workbook = this.addSheet(workbook, worksheet, name);

            return workbook;
        },
        async createDisclaimer(pdf) {
            pdf = await this.createPage(pdf, this.$t('disclaimer'));
            pdf.$newLine(0.5);
            pdf = await this.createParagraph(pdf, this.$t('pdf_disclaimer_0'), { size: 8 });
            pdf.$newLine();
            pdf = await this.createParagraph(pdf, this.$t('pdf_disclaimer_1'), { size: 8 });
            pdf.$newLine();
            pdf = await this.createParagraph(pdf, this.$t('pdf_disclaimer_2'), { size: 8 });
            pdf.$newLine();
            pdf = await this.createParagraph(pdf, this.$t('pdf_disclaimer_3'), { size: 8 });
            pdf.$newLine();
            pdf = await this.createParagraph(pdf, this.$t('pdf_disclaimer_4'), { size: 8 });
            pdf.$newLine();
            return pdf;
        },
        async appendHtmlToPdf(pdf, html = null) {
            pdf = createHtml(pdf, html);
            return pdf;
        },
        async insertInvestorInfo(pdf, investor) {
            if (investor && investor instanceof Investor) {
                investor = await Investor.$get(investor.id);

                const columns = pdf.$createColumns({ columns: 3 });

                pdf.$setColumn(columns[0]);
                pdf = await this.createInput(pdf, { label: this.$t('name'), value: investor.name });
                columns[0].height = pdf.positionY;

                pdf.$setColumn(columns[1]);
                pdf = await this.createInput(pdf, {
                    label: this.$t('category'),
                    value: investor.category ? this.$t(`enums.investor_category.${investor.category}`) : null
                });
                columns[1].height = pdf.positionY;

                pdf.$setColumn(columns[2]);
                pdf = await this.createInput(pdf, {
                    label: this.$t('phone_number'),
                    value: investor.phone_number ? investor.phone_number.computed_name : null
                });
                columns[2].height = pdf.positionY;

                pdf.$setColumn();
                pdf.positionY = Math.max(...columns.map(c => c.height));

                pdf = await this.createInput(pdf, {
                    label: this.$t('address'),
                    value: investor.address ? investor.address : null
                });
            }

            return pdf;
        },
        async createAltHeading(pdf, text, options = {}) {
            options = {
                size: 16,
                colour: this.default_colours.primaryText,
                background: this.default_colours.primaryLight,
                weight: 'bold',
                padding: { t: 9, r: 12, b: 6, l: 30 },
                ...options
            };

            const x = pdf.positionX;
            const y = pdf.positionY;

            pdf = await this.createParagraph(pdf, text, {
                size: options.size,
                colour: options.colour,
                weight: options.weight
            });

            pdf.setFillColor(options.background);
            pdf.rect(
                0,
                y,
                pdf.$_lastParagraphWidth + options.padding.r + options.padding.l,
                pdf.$_lastParagraphHeight + options.padding.t + options.padding.b,
                'F'
            );

            pdf.positionX = x;
            pdf.positionY = y + options.padding.t;

            pdf = await this.createParagraph(pdf, text, {
                size: options.size,
                colour: options.colour,
                weight: options.weight
            });

            return pdf;
        }
    }
};
